
import { useEffect } from 'react';

export default function HomePage() {
  useEffect(() => {
    // Smooth scroll for anchor links
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href^="#"]');
      if (anchor) {
        e.preventDefault();
        const href = anchor.getAttribute('href');
        if (href) {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }
      }
    };

    document.addEventListener('click', handleClick);

    // Scroll reveal animation
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe all elements with scroll-reveal class
    const revealElements = document.querySelectorAll('.scroll-reveal');
    revealElements.forEach((el) => observer.observe(el));

    return () => {
      document.removeEventListener('click', handleClick);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/5dd8a046f6ce6bd124e894ee246474f3/28950ea899de38841b2011062e46a7df.png" 
            alt="CENTREJUFE Background"
            className="w-full h-full object-cover object-center"
          />
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-blue-800/75 to-teal-700/80"></div>
        </div>

        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-400/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 text-center text-white relative z-10 py-12 md:py-20">
          <div className="max-w-5xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 md:px-6 md:py-3 rounded-full mb-6 md:mb-8 border border-white/30">
              <i className="ri-award-line text-lg md:text-2xl text-yellow-300"></i>
              <span className="text-xs md:text-base font-semibold">Eleições ASSEJUFE 2025</span>
            </div>

            <h1 className="text-4xl md:text-7xl lg:text-8xl font-extrabold mb-4 md:mb-6 leading-tight tracking-tight">
              <span className="bg-gradient-to-r from-white via-blue-100 to-teal-100 bg-clip-text text-transparent">
                Chapa Renovação
              </span>
            </h1>

            <div className="mb-6 md:mb-8">
              <p className="text-lg md:text-3xl lg:text-4xl font-bold mb-2 md:mb-3 text-blue-100">
                Candidatos à Diretoria da ASSEJUFE
              </p>
              <div className="flex items-center justify-center gap-2 md:gap-3 text-xs md:text-lg lg:text-xl opacity-90">
                <i className="ri-building-line text-teal-300"></i>
                <p>Associação dos Servidores da Justiça Federal da 1ª Região</p>
              </div>
            </div>

            <div className="max-w-4xl mx-auto mb-6 md:mb-10">
              <div className="bg-white/15 backdrop-blur-lg rounded-2xl md:rounded-3xl p-5 md:p-10 border border-white/30 shadow-2xl hover:bg-white/20 hover:scale-105 transition-all duration-500">
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-400 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-lightbulb-flash-line text-xl md:text-2xl text-white"></i>
                  </div>
                  <div className="text-left">
                    <h3 className="text-base md:text-2xl font-bold mb-2 md:mb-3 text-white">Nosso Compromisso</h3>
                    <p className="text-sm md:text-lg leading-relaxed text-white">
                      Nossa chapa tem o compromisso com a <strong>RENOVAÇÃO</strong> do relacionamento da Assejufe com o Servidor, com foco em ações que visem o seu <strong>bem estar</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-3 md:gap-5 mb-8 md:mb-12">
              <a
                href="#propostas"
                className="group inline-flex items-center gap-2 md:gap-3 bg-white text-blue-900 px-5 py-3 md:px-10 md:py-5 rounded-full text-sm md:text-lg font-bold hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:shadow-blue-500/50 transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-list-3-line text-base md:text-xl group-hover:rotate-12 transition-transform"></i>
                Nossas Propostas
              </a>
              <a
                href="#porque-renovar"
                className="group inline-flex items-center gap-2 md:gap-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white px-5 py-3 md:px-10 md:py-5 rounded-full text-sm md:text-lg font-bold hover:from-teal-600 hover:to-teal-700 transition-all duration-300 shadow-2xl hover:shadow-teal-500/50 transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line text-base md:text-xl group-hover:rotate-180 transition-transform duration-500"></i>
                Por Que Renovar?
              </a>
              <a
                href="#equipe"
                className="group inline-flex items-center gap-2 md:gap-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-3 md:px-10 md:py-5 rounded-full text-sm md:text-lg font-bold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-2xl hover:shadow-blue-500/50 transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-team-line text-base md:text-xl group-hover:scale-110 transition-transform"></i>
                Nossa Equipe
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-md rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/20 hover:bg-white/20 hover:scale-105 hover:shadow-2xl transition-all duration-300 cursor-pointer">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <i className="ri-team-line text-xl md:text-2xl text-white"></i>
                </div>
                <h4 className="text-base md:text-lg font-bold mb-1 md:mb-2">Gestão Participativa</h4>
                <p className="text-xs md:text-sm text-blue-100">Decisões transparentes com os associados</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/20 hover:bg-white/20 hover:scale-105 hover:shadow-2xl transition-all duration-300 cursor-pointer">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-teal-400 to-teal-600 rounded-xl flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <i className="ri-building-2-line text-xl md:text-2xl text-white"></i>
                </div>
                <h4 className="text-base md:text-lg font-bold mb-1 md:mb-2">Infraestrutura Moderna</h4>
                <p className="text-xs md:text-sm text-blue-100">Renovação completa do CENTREJUFE</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/20 hover:bg-white/20 hover:scale-105 hover:shadow-2xl transition-all duration-300 cursor-pointer">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <i className="ri-calendar-event-line text-xl md:text-2xl text-white"></i>
                </div>
                <h4 className="text-base md:text-lg font-bold mb-1 md:mb-2">Eventos Regulares</h4>
                <p className="text-xs md:text-sm text-blue-100">Programação diversificada de atividades</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Propostas Section */}
      <section id="propostas" className="py-10 md:py-20 bg-gradient-to-b from-white to-blue-50">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="scroll-reveal text-2xl md:text-5xl font-bold text-center mb-2 md:mb-4 text-blue-900 opacity-0">
            Nossas Propostas
          </h2>
          <p className="scroll-reveal text-sm md:text-xl text-center text-gray-600 mb-8 md:mb-16 max-w-3xl mx-auto opacity-0">
            Comprometidos com a renovação e valorização do CENTREJUFE
          </p>

          <div className="mb-10 md:mb-16">
            <div className="scroll-reveal bg-white rounded-2xl md:rounded-3xl shadow-xl p-5 md:p-8 hover:shadow-2xl transition-all duration-300 border-t-4 border-blue-600 opacity-0">
              <div className="text-center mb-6 md:mb-12">
                <h3 className="text-xl md:text-4xl font-bold text-blue-800 mb-2 md:mb-4">
                  1. Recuperação do Espaço do CENTREJUFE
                </h3>
                <p className="text-xs md:text-lg text-gray-600">
                  Revitalização completa para melhor acolher os associados
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
                {[
                  { icon: 'ri-fire-line', title: 'Reforma das Churrasqueiras', desc: 'Modernização completa das áreas de churrasqueira para melhor atender os associados' },
                  { icon: 'ri-basketball-line', title: 'Reforma das Quadras', desc: 'Revitalização das quadras esportivas com nova infraestrutura e equipamentos' },
                  { icon: 'ri-ping-pong-line', title: 'Quadra de Beach Tennis', desc: 'Construção de quadra de beach tennis para diversificar as opções esportivas' },
                  { icon: 'ri-temp-hot-line', title: 'Reformar Sauna Seca', desc: 'Recuperação e modernização da sauna seca para relaxamento dos associados' },
                  { icon: 'ri-gamepad-line', title: 'Reativar Salão de Jogos', desc: 'Reativação do salão de jogos com novos equipamentos e ambiente renovado' },
                  { icon: 'ri-run-line', title: 'Instalação de Academia', desc: 'Implantação de academia completa com equipamentos modernos para atividades físicas' }
                ].map((item, index) => (
                  <div key={index} className="bg-white rounded-xl md:rounded-2xl p-4 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 border border-blue-100 cursor-pointer">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center mb-3 md:mb-6 transition-transform duration-300 hover:rotate-12">
                      <i className={`${item.icon} text-xl md:text-3xl text-white`}></i>
                    </div>
                    <h4 className="text-base md:text-xl font-bold text-gray-800 mb-2 md:mb-3">{item.title}</h4>
                    <p className="text-xs md:text-base text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="scroll-reveal bg-white rounded-2xl md:rounded-3xl shadow-xl p-5 md:p-8 hover:shadow-2xl transition-all duration-300 border-t-4 border-teal-600 opacity-0">
              <div className="text-center mb-6 md:mb-12">
                <h3 className="text-xl md:text-4xl font-bold text-blue-800 mb-2 md:mb-4">
                  2. Atividades e Eventos
                </h3>
                <p className="text-xs md:text-lg text-gray-600">
                  Programação diversificada para integração e bem-estar
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
                {[
                  { icon: 'ri-music-2-line', title: 'Festas Temáticas Regulares', desc: 'Organização de eventos temáticos periódicos para integração e lazer dos associados' },
                  { icon: 'ri-footprint-line', title: 'Aulas de Danças', desc: 'Oferta de aulas de diversos estilos de dança para todas as idades' },
                  { icon: 'ri-heart-pulse-line', title: 'Parceria com SECBE', desc: 'Desenvolvimento de ações destinadas ao bem-estar do servidor em parceria com a SECBE' },
                  { icon: 'ri-trophy-line', title: 'Olimpíadas da Primeira Região', desc: 'Realização de olimpíadas integrando servidores de toda a primeira região' }
                ].map((item, index) => (
                  <div key={index} className="bg-white rounded-xl md:rounded-2xl p-4 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 border border-blue-100 cursor-pointer">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center mb-3 md:mb-6 transition-transform duration-300 hover:rotate-12">
                      <i className={`${item.icon} text-xl md:text-3xl text-white`}></i>
                    </div>
                    <h4 className="text-base md:text-xl font-bold text-gray-800 mb-2 md:mb-3">{item.title}</h4>
                    <p className="text-xs md:text-base text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Por Que Renovar Section */}
      <section id="porque-renovar" className="py-10 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="scroll-reveal text-2xl md:text-5xl font-bold text-center mb-2 md:mb-4 text-blue-900 opacity-0">
              Por Que Renovar?
            </h2>
            <p className="scroll-reveal text-sm md:text-xl text-center text-gray-600 mb-6 md:mb-12 max-w-3xl mx-auto opacity-0">
              A alternância de gestão é fundamental para a evolução e fortalecimento da nossa associação
            </p>

            <div className="scroll-reveal bg-gradient-to-br from-blue-900 via-blue-800 to-teal-700 rounded-2xl md:rounded-3xl p-6 md:p-12 text-white mb-6 md:mb-12 shadow-2xl hover:scale-105 transition-all duration-500 opacity-0 cursor-pointer">
              <div className="flex items-center justify-center mb-4 md:mb-8">
                <div className="w-14 h-14 md:w-20 md:h-20 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-refresh-line text-3xl md:text-5xl"></i>
                </div>
              </div>
              <h3 className="text-xl md:text-3xl font-bold text-center mb-3 md:mb-6">
                Alternância Saudável de Gestão
              </h3>
              <p className="text-sm md:text-lg leading-relaxed text-center opacity-95 mb-4 md:mb-8">
                Pensamos que é saudável a alternância de gestão, e por isso aceitamos o desafio de concorrer à Diretoria da ASSEJUFE.
              </p>
              <div className="h-px bg-white/30 mb-4 md:mb-8"></div>
              <p className="text-sm md:text-lg leading-relaxed text-center opacity-95">
                Entendemos que é possível contribuir para que, efetivamente, os objetivos da ASSEJUFE sejam alcançados.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-12">
              {[
                { icon: 'ri-lightbulb-line', title: 'Novas Ideias', desc: 'Renovação traz perspectivas frescas e soluções inovadoras para os desafios da associação' },
                { icon: 'ri-team-line', title: 'Participação Ativa', desc: 'Alternância estimula o engajamento e a participação de mais associados na gestão' },
                { icon: 'ri-line-chart-line', title: 'Evolução Contínua', desc: 'Mudança de gestão promove crescimento institucional e aperfeiçoamento constante' }
              ].map((item, index) => (
                <div key={index} className="scroll-reveal bg-blue-50 rounded-xl md:rounded-2xl p-5 md:p-8 text-center hover:shadow-lg hover:scale-105 transition-all duration-300 opacity-0 cursor-pointer">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-blue-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-5 transition-transform duration-300 hover:rotate-12">
                    <i className={`${item.icon} text-xl md:text-3xl text-white`}></i>
                  </div>
                  <h4 className="text-base md:text-xl font-bold text-blue-900 mb-2 md:mb-3">{item.title}</h4>
                  <p className="text-xs md:text-base text-gray-700 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="scroll-reveal bg-gradient-to-r from-teal-600 to-blue-600 rounded-2xl md:rounded-3xl p-6 md:p-12 text-white hover:scale-105 transition-all duration-500 opacity-0 cursor-pointer">
              <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
                <div className="w-16 h-16 md:w-24 md:h-24 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-4-line text-3xl md:text-5xl"></i>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl md:text-3xl font-bold mb-3 md:mb-5">Nosso Foco: CENTREJUFE</h3>
                  <p className="text-sm md:text-lg leading-relaxed opacity-95 mb-3 md:mb-4">
                    Nosso trabalho terá como foco a melhoria do espaço de lazer do CENTREJUFE para proporcionar bem-estar de magistrados e servidores.
                  </p>
                  <p className="text-sm md:text-lg leading-relaxed opacity-95">
                    Um local de <strong>relaxamento</strong>, <strong>encontro</strong>, <strong>diversão</strong>, <strong>esporte</strong> e <strong>lazer</strong> para toda a comunidade da Justiça Federal da 1ª Região.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Equipe Section */}
      <section id="equipe" className="py-10 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="scroll-reveal text-2xl md:text-5xl font-bold text-center mb-2 md:mb-4 text-blue-900 opacity-0">
            Nossa Equipe
          </h2>
          <p className="scroll-reveal text-sm md:text-xl text-center text-gray-600 mb-8 md:mb-16 opacity-0">
            Conheça os membros da Chapa Renovação
          </p>

          <div className="mb-10 md:mb-16">
            <h3 className="scroll-reveal text-xl md:text-3xl font-bold text-center mb-6 md:mb-12 text-blue-800 opacity-0">
              Diretoria
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 max-w-6xl mx-auto">
              {[
                { name: 'CARMELITA HENRIQUE MOREIRA', role: 'Presidente', img: 'https://static.readdy.ai/image/5dd8a046f6ce6bd124e894ee246474f3/ab1ce8ccc4c154bc9ee700658da6fc4b.jpeg' },
                { name: 'NÁDIA BARBOSA DA CRUZ SANTANA', role: 'Vice-Presidente', img: 'https://static.readdy.ai/image/babe789ed2cb25448a96b6e78471d858/3f11b2dab226263c7e6e2d9770540ebf.jpeg' },
                { name: 'GUARANDY FIGUEIREDO DA NÓBREGA', role: 'Diretor de Relações Públicas', img: 'https://static.readdy.ai/image/babe789ed2cb25448a96b6e78471d858/c74564dc8e8d24abc88764bacff49d95.jpeg' },
                { name: 'CÉLIA MARIA MEDEIROS', role: 'Diretora de Assuntos Jurídicos', img: 'https://static.readdy.ai/image/babe789ed2cb25448a96b6e78471d858/3ace2158578c279a4d685f2391231c46.jpeg' },
                { name: 'MARÍLIA GEREMIAS DE FREITAS', role: 'Diretora Social', img: 'https://static.readdy.ai/image/babe789ed2cb25448a96b6e78471d858/ab8e40494ac5d6b7db14ee4f105c5319.jpeg' },
                { name: 'MARIA REIS SILVEIRA BRAGA COSTA', role: 'Diretora Administrativa', img: 'https://static.readdy.ai/image/babe789ed2cb25448a96b6e78471d858/a4b8a0af1fa8f174f133fc28b35f0a6e.jpeg' },
                { name: 'FÁBIO JOÃO GAUDINE COSTA', role: 'Diretor Financeiro', img: 'https://static.readdy.ai/image/babe789ed2cb25448a96b6e78471d858/a42cca9775e6317b12111d6b919200b8.jpeg' },
                { name: 'MONICA REGINA FERREIRA RODRIGUES', role: 'Diretor de Esportes', img: 'https://static.readdy.ai/image/5dd8a046f6ce6bd124e894ee246474f3/486bd036e3dc3e932fd6d99518aaea63.jpeg' },
                { name: 'KÁTIA SOARES PIRES', role: 'Diretora Secretária', img: 'https://static.readdy.ai/image/babe789ed2cb25448a96b6e78471d858/7cf0fdab57887f8f04c954a50b185629.jpeg' }
              ].map((member, index) => (
                <div key={index} className="scroll-reveal bg-gradient-to-br from-blue-50 to-white rounded-xl md:rounded-2xl shadow-lg p-3 md:p-6 hover:shadow-xl hover:scale-105 transition-all duration-300 border border-blue-100 opacity-0 cursor-pointer">
                  <div className="aspect-square overflow-hidden bg-gray-100 rounded-lg md:rounded-xl mb-2 md:mb-4">
                    <img
                      alt={member.name}
                      className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500"
                      src={member.img}
                    />
                  </div>
                  <div className="text-center">
                    <h4 className="text-xs md:text-lg font-bold text-blue-900 mb-1 md:mb-2">{member.role}</h4>
                    <p className="text-xs md:text-base text-gray-700 font-semibold">{member.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="scroll-reveal text-xl md:text-3xl font-bold text-center mb-6 md:mb-12 text-teal-800 opacity-0">
              Conselho Fiscal
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 max-w-6xl mx-auto">
              {[
                { role: '1º Titular', name: 'MARA LUCIA MARTINS DE ARAÚJO BESSA' },
                { role: '2º Titular', name: 'WASHIGNTON HENRIQUE CARVALHO ALMEIDA' },
                { role: '3º Titular', name: 'ÉDILA ROSE MONTEIRO DE SOUZA' },
                { role: '4º Titular', name: 'LADINILSON DE OLIVEIRA CARVALHO' },
                { role: '5º Titular', name: 'WILLER LARRY DE OLIVEIRA PEREIRA' },
                { role: '1º Suplente', name: 'RITA PAULA COSTA DE LIMA' },
                { role: '2º Suplente', name: 'FÁTIMA DE MARIA PRAZERES RODRIGUES' },
                { role: '3º Suplente', name: 'DELVITE VALVERDE CORREIA' }
              ].map((member, index) => (
                <div key={index} className="scroll-reveal bg-gradient-to-br from-teal-50 to-white rounded-xl md:rounded-2xl shadow-lg p-3 md:p-6 hover:shadow-xl hover:scale-105 transition-all duration-300 border border-teal-100 opacity-0 cursor-pointer">
                  <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-600 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-line text-white text-base md:text-xl"></i>
                    </div>
                    <div>
                      <p className="text-xs md:text-sm font-bold text-blue-900 mb-1">{member.role}</p>
                      <p className="text-xs md:text-sm text-gray-700 font-semibold">{member.name}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Objetivos Section */}
      <section id="objetivos" className="py-10 md:py-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="scroll-reveal text-2xl md:text-5xl font-bold text-center mb-2 md:mb-4 text-blue-900 opacity-0">
            Objetivos da ASSEJUFE
          </h2>
          <p className="scroll-reveal text-sm md:text-xl text-center text-gray-600 mb-4 md:mb-8 max-w-3xl mx-auto opacity-0">
            Nossa missão é representar e promover o bem-estar dos servidores
          </p>

          <div className="scroll-reveal bg-gradient-to-r from-blue-600 to-teal-600 rounded-2xl md:rounded-3xl p-5 md:p-10 text-white max-w-5xl mx-auto mb-8 md:mb-16 hover:scale-105 transition-all duration-500 opacity-0 cursor-pointer">
            <div className="flex items-start gap-3 md:gap-6">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-heart-pulse-line text-xl md:text-3xl"></i>
              </div>
              <div>
                <h3 className="text-base md:text-2xl font-bold mb-2 md:mb-4">
                  Renovando Nosso Compromisso com Você
                </h3>
                <p className="text-xs md:text-base leading-relaxed opacity-95">
                  Nossa chapa está comprometida em resgatar e fortalecer os objetivos fundamentais da ASSEJUFE. 
                  Queremos ir além das palavras e transformar cada um desses compromissos em ações concretas que 
                  façam diferença no dia a dia dos associados. Com transparência, dedicação e trabalho em equipe, 
                  vamos renovar a confiança e construir uma associação mais forte, atuante e próxima de você.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6 max-w-6xl mx-auto mb-8 md:mb-16">
            {[
              'Representar os interesses dos seus associados perante o Tribunal Regional Federal da 1ª Região e demais órgãos das administrações pública e privada',
              'Promover a integração dos servidores do Tribunal Regional Federal da 1ª Região e da Justiça Federal de 1º Grau da 1ª Região',
              'Prestar a seus associados e dependentes, de forma direta ou complementar, assistência recreativa, cultural, esportiva, social, à saúde, educacional e jurídica',
              'Promover a divulgação das atividades dos associados quando envolverem assuntos de interesse geral',
              'Promover e estimular ações ligadas ao desenvolvimento cultural, científico e tecnológico do país, notadamente na área jurídica',
              'Manter intercâmbio com associações congêneres e afins'
            ].map((objective, index) => (
              <div key={index} className="scroll-reveal bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 hover:shadow-xl hover:scale-105 transition-all duration-300 border-l-4 border-blue-600 opacity-0 cursor-pointer">
                <div className="flex items-start gap-2 md:gap-4">
                  <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-blue-600 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xs md:text-base">{['I', 'II', 'III', 'IV', 'V', 'VI'][index]}</span>
                  </div>
                  <p className="text-xs md:text-base text-gray-700 leading-relaxed">{objective}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="scroll-reveal bg-gradient-to-r from-blue-600 to-teal-600 rounded-2xl md:rounded-3xl p-6 md:p-10 text-center text-white max-w-4xl mx-auto hover:scale-105 transition-all duration-500 opacity-0 cursor-pointer">
            <i className="ri-handshake-line text-3xl md:text-6xl mb-3 md:mb-6 block"></i>
            <h3 className="text-lg md:text-3xl font-bold mb-2 md:mb-4">Parcerias de Qualidade</h3>
            <p className="text-xs md:text-lg opacity-90 leading-relaxed">
              A ASSEJUFE está trabalhando para firmar as melhores parcerias e oferecer uma rede conveniada de qualidade a seus associados.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-900 text-white py-8 md:py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12 mb-6 md:mb-12">
            <div>
              <h3 className="text-lg md:text-2xl font-bold mb-2 md:mb-4">Chapa Renovação</h3>
              <p className="text-xs md:text-base text-blue-200 leading-relaxed">
                Candidata à Diretoria da ASSEJUFE - Associação dos Servidores da Justiça Federal da 1ª Região
              </p>
            </div>
            <div>
              <h4 className="text-base md:text-xl font-bold mb-2 md:mb-4">Links Rápidos</h4>
              <ul className="space-y-2">
                <li>
                  <a href="#propostas" className="text-xs md:text-base text-blue-200 hover:text-white transition-colors cursor-pointer">
                    Propostas
                  </a>
                </li>
                <li>
                  <a href="#equipe" className="text-xs md:text-base text-blue-200 hover:text-white transition-colors cursor-pointer">
                    Nossa Equipe
                  </a>
                </li>
                <li>
                  <a href="#objetivos" className="text-xs md:text-base text-blue-200 hover:text-white transition-colors cursor-pointer">
                    Objetivos
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-base md:text-xl font-bold mb-2 md:mb-4">Sede</h4>
              <p className="text-xs md:text-base text-blue-200 leading-relaxed">
                Clube CENTREJUFE<br />
                Brasília - DF
              </p>
            </div>
          </div>
          <div className="border-t border-blue-800 pt-4 md:pt-8 text-center">
            <p className="text-xs md:text-sm text-blue-300">
              © 2025 Chapa Renovação - ASSEJUFE. Todos os direitos reservados. |{' '}
              <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
